import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const DilemmaManagement = () => {
  const [dilemmas, setDilemmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDilemma, setEditingDilemma] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  
  // Bulk Import States
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkJsonInput, setBulkJsonInput] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  
  const [formData, setFormData] = useState({
    question: '',
    optionA: '',
    optionB: '',
    category: 'general',
    dilemmaDate: new Date().toISOString().split('T')[0],
    isActive: true
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const categories = [
    { value: 'player', label: '⚽ Player Dilemma' },
    { value: 'team', label: '🏆 Team Dilemma' },
    { value: 'fan-emotion', label: '❤️ Fan Emotion' },
    { value: 'crazy-fun', label: '🤪 Crazy & Fun' },
    { value: 'general', label: '🎯 General' }
  ];

  // Fetch dilemmas
  const fetchDilemmas = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/fun-dilemmas?page=${page}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDilemmas(response.data.dilemmas);
      setPagination({
        page: response.data.pagination.page,
        totalPages: response.data.pagination.totalPages,
        total: response.data.pagination.total
      });
    } catch (err) {
      console.error('Error fetching dilemmas:', err);
      setError('Failed to load dilemmas');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/fun-dilemmas/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchDilemmas();
    fetchStats();
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    if (!formData.question.trim() || !formData.optionA.trim() || !formData.optionB.trim()) {
      setError('Please fill in all required fields');
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      if (editingDilemma) {
        await axios.put(
          `${API_BASE_URL}/api/admin/fun-dilemmas/${editingDilemma._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Dilemma updated successfully!');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/admin/fun-dilemmas`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Dilemma created successfully!');
      }

      setShowForm(false);
      setEditingDilemma(null);
      resetForm();
      fetchDilemmas(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Error saving dilemma:', err);
      setError(err.response?.data?.message || 'Failed to save dilemma');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (dilemma) => {
    if (!window.confirm(`Are you sure you want to delete this dilemma?\n\n"${dilemma.question}"\n\nThis will also delete all votes for this dilemma.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${API_BASE_URL}/api/admin/fun-dilemmas/${dilemma._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Dilemma deleted successfully!');
      fetchDilemmas(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Error deleting dilemma:', err);
      setError('Failed to delete dilemma');
    }
  };

  // Handle toggle active
  const handleToggleActive = async (dilemma) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_BASE_URL}/api/admin/fun-dilemmas/${dilemma._id}`,
        { isActive: !dilemma.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDilemmas(pagination.page);
    } catch (err) {
      console.error('Error toggling dilemma status:', err);
      setError('Failed to update dilemma status');
    }
  };

  // Handle edit
  const handleEdit = (dilemma) => {
    setEditingDilemma(dilemma);
    setFormData({
      question: dilemma.question,
      optionA: dilemma.optionA,
      optionB: dilemma.optionB,
      category: dilemma.category,
      dilemmaDate: new Date(dilemma.dilemmaDate).toISOString().split('T')[0],
      isActive: dilemma.isActive
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      question: '',
      optionA: '',
      optionB: '',
      category: 'general',
      dilemmaDate: new Date().toISOString().split('T')[0],
      isActive: true
    });
    setEditingDilemma(null);
  };

  // ============ BULK IMPORT FUNCTIONS ============
  
  // Parse and validate JSON input
  const handleBulkJsonChange = (jsonStr) => {
    setBulkJsonInput(jsonStr);
    setBulkError('');
    setBulkPreview(null);
    
    if (!jsonStr.trim()) return;
    
    try {
      const parsed = JSON.parse(jsonStr);
      
      // Support both array format and object with dilemmas array
      let dilemmasArray = [];
      let dateFromJson = null;
      
      if (Array.isArray(parsed)) {
        dilemmasArray = parsed;
      } else if (parsed.dilemmas && Array.isArray(parsed.dilemmas)) {
        dilemmasArray = parsed.dilemmas;
        dateFromJson = parsed.dilemmaDate || parsed.date || null;
      } else if (parsed.question) {
        // Single dilemma object
        dilemmasArray = [parsed];
      } else {
        throw new Error('Invalid format. Expected an array of dilemmas or an object with "dilemmas" array.');
      }
      
      if (dilemmasArray.length === 0) {
        throw new Error('No dilemmas found in the JSON.');
      }
      
      // Validate each dilemma
      const validatedDilemmas = dilemmasArray.map((d, index) => {
        const errors = [];
        
        if (!d.question || typeof d.question !== 'string') {
          errors.push(`Item ${index + 1}: Missing or invalid "question"`);
        }
        if (!d.optionA || typeof d.optionA !== 'string') {
          errors.push(`Item ${index + 1}: Missing or invalid "optionA"`);
        }
        if (!d.optionB || typeof d.optionB !== 'string') {
          errors.push(`Item ${index + 1}: Missing or invalid "optionB"`);
        }
        
        if (errors.length > 0) {
          throw new Error(errors.join('\n'));
        }
        
        return {
          question: d.question.trim(),
          optionA: d.optionA.trim(),
          optionB: d.optionB.trim(),
          optionC: d.optionC ? d.optionC.trim() : undefined,
          category: d.category || 'general',
          questionType: d.questionType || 'classic',
          dailySlot: d.dailySlot || (index + 1),
          dilemmaDate: d.dilemmaDate || dateFromJson || new Date().toISOString().split('T')[0],
          isActive: d.isActive !== undefined ? d.isActive : true
        };
      });
      
      setBulkPreview({
        count: validatedDilemmas.length,
        dilemmas: validatedDilemmas,
        commonDate: dateFromJson
      });
      
    } catch (err) {
      if (err instanceof SyntaxError) {
        setBulkError('Invalid JSON format. Please check your syntax.');
      } else {
        setBulkError(err.message);
      }
    }
  };
  
  // Submit bulk import
  const handleBulkImport = async () => {
    if (!bulkPreview || bulkPreview.dilemmas.length === 0) {
      setBulkError('No valid dilemmas to import.');
      return;
    }
    
    try {
      setBulkImporting(true);
      setBulkError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/fun-dilemmas/bulk`,
        { dilemmas: bulkPreview.dilemmas },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(`✅ Successfully imported ${response.data.created || bulkPreview.dilemmas.length} dilemmas!`);
      setShowBulkImport(false);
      setBulkJsonInput('');
      setBulkPreview(null);
      fetchDilemmas(pagination.page);
      fetchStats();
      
    } catch (err) {
      console.error('Bulk import error:', err);
      setBulkError(err.response?.data?.message || 'Failed to import dilemmas. Please try again.');
    } finally {
      setBulkImporting(false);
    }
  };
  
  // Reset bulk import form
  const resetBulkImport = () => {
    setBulkJsonInput('');
    setBulkPreview(null);
    setBulkError('');
  };
  
  // Sample JSON template
  const sampleJson = `{
  "dilemmaDate": "${new Date().toISOString().split('T')[0]}",
  "dilemmas": [
    {
      "question": "World Cup Final খেলছো — কোনটা বেছে নেবে? 🏆",
      "optionA": "100 রান কিন্তু দল হারবে 😢",
      "optionB": "0 রান কিন্তু দল জিতবে 🎉",
      "category": "player",
      "questionType": "classic"
    },
    {
      "question": "Messi vs Ronaldo — চিরকালের debate! ⚽",
      "optionA": "Messi is the GOAT 🐐",
      "optionB": "Ronaldo is the GOAT 🐐",
      "category": "player",
      "questionType": "classic"
    },
    {
      "question": "Your favorite team — কোনটা চাও? 🤔",
      "optionA": "Win every league match but lose finals",
      "optionB": "Lose league matches but win every final",
      "category": "team",
      "questionType": "classic"
    },
    {
      "question": "Football fan হিসেবে কোনটা বেশি কষ্টের? 💔",
      "optionA": "Last minute goal এ হারা",
      "optionB": "Penalty shootout এ হারা",
      "category": "fan-emotion",
      "questionType": "classic"
    },
    {
      "question": "Crazy choice time! 🤪",
      "optionA": "Play with your idol but never win",
      "optionB": "Never meet your idol but always win",
      "category": "crazy-fun",
      "questionType": "classic"
    }
  ]
}`;

  // Get category label
  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-3xl">🤔</span>
            Fun Dilemma Management
          </h1>
          <p className="text-gray-500 mt-1">Create and manage daily sports dilemmas</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              resetBulkImport();
              setShowBulkImport(true);
              setError('');
              setSuccess('');
            }}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition-all flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Bulk Import JSON
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
              setError('');
              setSuccess('');
            }}
            className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition-all flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Dilemma
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Dilemmas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDilemmas}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Active Dilemmas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeDilemmas}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🗳️</span>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Votes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalVotes?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📈</span>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Avg Votes/Dilemma</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalDilemmas > 0 
                    ? Math.round(stats.totalVotes / stats.totalDilemmas) 
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="text-xl">⚠️</span>
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="text-xl">✅</span>
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">✕</button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingDilemma ? '✏️ Edit Dilemma' : '➕ Create New Dilemma'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingDilemma(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Question */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="e.g., World Cup Final খেলছো — কোনটা বেছে নেবে?"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none transition-all resize-none"
                  required
                />
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded-full text-xs mr-2">A</span>
                    Option A <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.optionA}
                    onChange={(e) => setFormData({ ...formData, optionA: e.target.value })}
                    placeholder="e.g., 100 রান কিন্তু দল হারবে"
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-500 text-white rounded-full text-xs mr-2">B</span>
                    Option B <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.optionB}
                    onChange={(e) => setFormData({ ...formData, optionB: e.target.value })}
                    placeholder="e.g., 10 রান কিন্তু দল জিতবে"
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all resize-none"
                    required
                  />
                </div>
              </div>

              {/* Category & Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none transition-all bg-white"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Scheduled Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.dilemmaDate}
                    onChange={(e) => setFormData({ ...formData, dilemmaDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                </label>
                <span className="text-sm font-medium text-gray-700">Active</span>
              </div>

              {/* Preview */}
              {formData.question && (
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-4 border border-pink-100">
                  <p className="text-xs font-semibold text-pink-600 uppercase tracking-wider mb-2">Preview</p>
                  <p className="font-bold text-gray-800 mb-3">{formData.question}</p>
                  <div className="space-y-2">
                    <div className="bg-blue-500/10 border border-blue-200 rounded-lg p-2 flex items-center gap-2">
                      <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">A</span>
                      <span className="text-gray-700 text-sm">{formData.optionA || 'Option A...'}</span>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-200 rounded-lg p-2 flex items-center gap-2">
                      <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">B</span>
                      <span className="text-gray-700 text-sm">{formData.optionB || 'Option B...'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingDilemma(null);
                    resetForm();
                  }}
                  className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      {editingDilemma ? '💾 Update Dilemma' : '✨ Create Dilemma'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  Bulk Import Dilemmas (JSON)
                </h2>
                <button
                  onClick={() => {
                    setShowBulkImport(false);
                    resetBulkImport();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="text-gray-500 mt-1 text-sm">
                Paste JSON to add multiple dilemmas at once. Supports up to 50 dilemmas per import.
              </p>
            </div>

            <div className="p-6 space-y-5">
              {/* Error Display */}
              {bulkError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-xl">⚠️</span>
                    <pre className="text-sm whitespace-pre-wrap font-mono">{bulkError}</pre>
                  </div>
                </div>
              )}

              {/* JSON Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    JSON Input <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => handleBulkJsonChange(sampleJson)}
                    className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Load Sample Template
                  </button>
                </div>
                <textarea
                  value={bulkJsonInput}
                  onChange={(e) => handleBulkJsonChange(e.target.value)}
                  placeholder={`Paste your JSON here...\n\nExample format:\n{\n  "dilemmaDate": "2026-01-10",\n  "dilemmas": [\n    { "question": "...", "optionA": "...", "optionB": "..." },\n    ...\n  ]\n}`}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all resize-none font-mono text-sm bg-gray-50"
                />
              </div>

              {/* Format Guide */}
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                  <span>📖</span> JSON Format Guide
                </h4>
                <div className="text-sm text-purple-700 space-y-2">
                  <p><strong>Required fields:</strong> <code className="bg-purple-100 px-1 rounded">question</code>, <code className="bg-purple-100 px-1 rounded">optionA</code>, <code className="bg-purple-100 px-1 rounded">optionB</code></p>
                  <p><strong>Optional fields:</strong> <code className="bg-purple-100 px-1 rounded">optionC</code>, <code className="bg-purple-100 px-1 rounded">category</code>, <code className="bg-purple-100 px-1 rounded">questionType</code>, <code className="bg-purple-100 px-1 rounded">dailySlot</code>, <code className="bg-purple-100 px-1 rounded">dilemmaDate</code></p>
                  <p><strong>Categories:</strong> player, team, fan-emotion, crazy-fun, general</p>
                  <p><strong>Question types:</strong> classic, would-you-rather, hot-take</p>
                </div>
              </div>

              {/* Preview */}
              {bulkPreview && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-green-800 flex items-center gap-2">
                      <span>✅</span> Preview - {bulkPreview.count} Dilemmas Ready
                    </h4>
                    {bulkPreview.commonDate && (
                      <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        📅 {bulkPreview.commonDate}
                      </span>
                    )}
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {bulkPreview.dilemmas.map((d, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-green-100">
                        <div className="flex items-start gap-2">
                          <span className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 text-sm mb-1">{d.question}</p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">A: {d.optionA.substring(0, 30)}...</span>
                              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">B: {d.optionB.substring(0, 30)}...</span>
                              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{d.category}</span>
                              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">📅 {d.dilemmaDate}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkImport(false);
                    resetBulkImport();
                  }}
                  className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkImport}
                  disabled={bulkImporting || !bulkPreview || bulkPreview.dilemmas.length === 0}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {bulkImporting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Import {bulkPreview?.count || 0} Dilemmas
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dilemmas List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-700">All Dilemmas ({pagination.total || 0})</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-10 h-10 border-3 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500">Loading dilemmas...</p>
          </div>
        ) : dilemmas.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-5xl mb-3">🤔</div>
            <p className="text-gray-500 mb-4">No dilemmas found. Create your first one!</p>
            <button
              onClick={() => setShowForm(true)}
              className="text-pink-600 font-semibold hover:text-pink-700"
            >
              + Add New Dilemma
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {dilemmas.map((dilemma) => {
              const totalVotes = dilemma.votesA + dilemma.votesB;
              const percentA = totalVotes > 0 ? Math.round((dilemma.votesA / totalVotes) * 100) : 50;
              const percentB = totalVotes > 0 ? Math.round((dilemma.votesB / totalVotes) * 100) : 50;
              const isToday = new Date(dilemma.dilemmaDate).toDateString() === new Date().toDateString();
              
              return (
                <div key={dilemma._id} className={`p-5 hover:bg-gray-50 transition-colors ${isToday ? 'bg-pink-50/50' : ''}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Main Content */}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-sm bg-gray-100 px-2 py-1 rounded-full">
                          {getCategoryLabel(dilemma.category)}
                        </span>
                        <span className="text-sm text-gray-500">
                          📅 {formatDate(dilemma.dilemmaDate)}
                        </span>
                        {isToday && (
                          <span className="text-xs bg-pink-500 text-white px-2 py-1 rounded-full">
                            TODAY
                          </span>
                        )}
                        {!dilemma.isActive && (
                          <span className="text-xs bg-gray-300 text-gray-600 px-2 py-1 rounded-full">
                            INACTIVE
                          </span>
                        )}
                      </div>
                      
                      <h4 className="font-bold text-gray-800 mb-3">{dilemma.question}</h4>
                      
                      {/* Options with progress */}
                      <div className="space-y-2 mb-3">
                        <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                          <div 
                            className="absolute inset-y-0 left-0 bg-blue-500/20"
                            style={{ width: `${percentA}%` }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-between px-3">
                            <span className="text-sm text-gray-700 font-medium flex items-center gap-1">
                              <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">A</span>
                              {dilemma.optionA}
                            </span>
                            <span className="text-sm font-bold text-blue-600">{percentA}%</span>
                          </div>
                        </div>
                        <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                          <div 
                            className="absolute inset-y-0 left-0 bg-orange-500/20"
                            style={{ width: `${percentB}%` }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-between px-3">
                            <span className="text-sm text-gray-700 font-medium flex items-center gap-1">
                              <span className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">B</span>
                              {dilemma.optionB}
                            </span>
                            <span className="text-sm font-bold text-orange-600">{percentB}%</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-500">
                        🗳️ {totalVotes.toLocaleString()} votes • Created by {dilemma.createdBy?.username || 'Admin'}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2">
                      <button
                        onClick={() => handleToggleActive(dilemma)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          dilemma.isActive 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {dilemma.isActive ? '✓ Active' : '○ Inactive'}
                      </button>
                      <button
                        onClick={() => handleEdit(dilemma)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-all"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(dilemma)}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-all"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-center gap-2">
            <button
              onClick={() => fetchDilemmas(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ← Previous
            </button>
            <span className="text-sm text-gray-500 px-4">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => fetchDilemmas(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-100">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <span className="text-xl">💡</span> Tips for Creating Great Dilemmas
        </h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>• Keep questions short, engaging, and relatable to sports fans</li>
          <li>• Both options should be equally appealing/difficult to choose</li>
          <li>• Use emojis in questions to make them more fun and readable</li>
          <li>• Schedule dilemmas in advance to ensure daily content</li>
          <li>• Mix different categories to keep content diverse</li>
        </ul>
      </div>
    </div>
  );
};

export default DilemmaManagement;
